import type { LineType, Unit } from 'dxf-writer'
import type { ICADMetadata } from '@/plugins/cad/module/CADModule/manager/DataManager'
import { ColorMethod, EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import {
  type AlignDimensionEntity,
  type AngularDimensionEntity,
  type ArcEntity,
  type CircleEntity,
  type EllipseEntity,
  getAngularDimensionData,
  getAngularDimensionLegLines,
  getMLeaderBranchPoints,
  getMLeaderLandingEnd,
  getMLeaderLandingVector,
  type IEntity,
  type MLeaderEntity,
  type MTextEntity,
  type PolylineEntity,
  type RayEntity,
  type SplineEntity,
} from '@/plugins/cad/module/CADModule/model/entity'
import type { Layer } from '@/plugins/cad/module/CADModule/model/layer/Layer'
import { IgnoreLayerSet } from '@/plugins/cad/module/parser/DxfParser'
import { ArcUtils } from '@/plugins/cad/module/utils/ArcUtils'
import { getLoggerManager } from '@/shared/logging'
import type { DisplayUnit } from '@/shared/unitPresentation'
import AlignDimension from './AlignDimension'
import Arc from './Arc'
import AngularDimension from './angularDimension'
import Block from './Block'
import Circle from './Circle'
import {
  CADStandardLineWidth,
  DimStyleName,
  DXFLineWidth,
  getComplexLineType,
  LineTypeMapping,
} from './constant'
import DimStyle from './DimStyle'
import Drawing from './Drawing'
import { registerFrameImage } from './DxfImageRegistry'
import DimensionUtils from './dimensionUtils'
import {
  createDxfExportContext,
  createLegacyDxfExportContext,
  type DxfExportContext,
  type DxfExporterOptions,
  isDxfExporterOptions,
} from './dxfExportContext'
import { DxfFrameBuilder, type DxfFrameBuilderOptions, FRAME_PAPER_SIZES } from './dxfFrameBuilder'
import Ellipse from './ellipse'
import Line from './Line'
import MText from './MText'
import MLeader from './mLeader'
import Polyline from './Polyline'
import Ray from './ray'
import Spline from './Spline'
import Solid from './solid'

export type { DxfExporterOptions } from './dxfExportContext'

const log = getLoggerManager().getLogger('cad')

const DIMENSION_PRECISIONS: Record<DisplayUnit, number> = {
  m: 3,
  cm: 1,
  mm: 0,
  in: 2,
  ft: 2,
}

/**
 * CAD图元组码
 * 6 -线型名称
 * 62 -颜色索引
 * 370 -线宽(单位为百分之一毫米)
 * 420 -真实颜色值
 */

/**
 * CAD 图纸 DXF 导出器
 */
export class DxfExporter {
  /**
   * 将米制 CAD 元数据导出为 DXF 字符串。
   *
   * 配置对象路径根据目标显示格式自动换算模型坐标并设置全局长度显示，默认导出为米；
   * 英尺—英寸遵循 DXF Architectural 约定，内部坐标单位使用英寸。
   * 旧位置参数重载继续支持调用方指定 DXF 单位和坐标倍率。
   *
   * @param data - 待导出的 CAD 元数据，空间数据固定按米解释
   * @param options - 目标单位与英尺展示格式，默认使用米和十进制格式
   * @returns 完整的 DXF 文本
   */
  static export(data: ICADMetadata, options?: DxfExporterOptions): string
  /** @deprecated 使用配置对象；旧签名继续支持任意手工倍率。 */
  static export(data: ICADMetadata, units?: Unit, scale?: number): string
  static export(
    data: ICADMetadata,
    optionsOrUnits?: DxfExporterOptions | Unit,
    scale: number = 1
  ): string {
    const context = isDxfExporterOptions(optionsOrUnits)
      ? createDxfExportContext(optionsOrUnits, 'm')
      : optionsOrUnits === undefined
        ? createDxfExportContext(undefined, 'm')
        : createLegacyDxfExportContext(optionsOrUnits, scale)
    const { drawing, entities } = DxfExporter.buildDrawing(data, context)
    DxfExporter.setViewport(drawing, entities, context.modelScale)
    return drawing.toDxfString()
  }

  /**
   * 将米制 CAD 元数据导出为带图框的 DXF 文件。
   *
   * 模型按目标显示格式换算；图框保持毫米纸张规范，并按图纸比例及实际 DXF
   * 坐标单位生成。英尺—英寸遵循 DXF Architectural 约定，内部坐标单位使用英寸。
   * 以所有图元的包围盒中心为图框中心，生成 BLOCK "FRAME" 并插入 model space。
   * 若 frameOptions.logoPath 非空，同时向 DXF 字符串注入 IMAGE / IMAGEDEF 实体。
   *
   * @param data - 待导出的 CAD 元数据，空间数据固定按米解释
   * @param frameOptions - 图框样式、纸张及图纸比例配置
   * @param options - 目标单位与英尺展示格式；未提供时默认使用毫米
   * @returns 含图框的完整 DXF 文本
   */
  static exportWithFrame(
    data: ICADMetadata,
    frameOptions: DxfFrameBuilderOptions,
    options?: DxfExporterOptions
  ): string
  /** @deprecated 使用配置对象；旧签名继续支持任意手工倍率。 */
  static exportWithFrame(
    data: ICADMetadata,
    frameOptions: DxfFrameBuilderOptions,
    units?: Unit,
    scale?: number
  ): string
  static exportWithFrame(
    data: ICADMetadata,
    frameOptions: DxfFrameBuilderOptions,
    optionsOrUnits?: DxfExporterOptions | Unit,
    scale: number = 1000
  ): string {
    const context = isDxfExporterOptions(optionsOrUnits)
      ? createDxfExportContext(optionsOrUnits, 'mm')
      : optionsOrUnits === undefined
        ? createDxfExportContext(undefined, 'mm')
        : createLegacyDxfExportContext(optionsOrUnits, scale)
    const { drawing, entities } = DxfExporter.buildDrawing(data, context)

    // 计算图元包围盒，将图元居中对齐到图框可绘图区域
    const bounds = DxfExporter.calculateBounds(entities, context.modelScale)
    const [pw, ph] = FRAME_PAPER_SIZES[frameOptions.paperSize] ?? [420, 297]
    const frameCoordinateScale = frameOptions.scale * context.millimeterScale
    const W = pw * frameCoordinateScale
    const H = ph * frameCoordinateScale

    // 与 dxfFrameBuilder 保持一致的内边距与标题栏尺寸（单位：模型单位）
    const s = frameCoordinateScale
    const ml = 25 * s // 左侧装订边
    const m = 5 * s // 其余边距
    const tbWidth = 50 * s // 标题栏宽度（与 DxfFrameBuilder 保持一致）

    // 可绘图区域中心（相对图框原点）
    const drawableCenterX = (ml + (W - m - tbWidth)) / 2
    const drawableCenterY = H / 2

    let frameOriginX = -drawableCenterX
    let frameOriginY = -drawableCenterY
    if (bounds) {
      const entityCenterX = (bounds.min.x + bounds.max.x) / 2
      const entityCenterY = (bounds.min.y + bounds.max.y) / 2
      frameOriginX = entityCenterX - drawableCenterX
      frameOriginY = entityCenterY - drawableCenterY
    }

    const imageArtifacts = DxfFrameBuilder.generate(
      drawing,
      frameOptions,
      {
        x: frameOriginX,
        y: frameOriginY,
      },
      frameCoordinateScale
    )
    registerFrameImage(drawing, imageArtifacts)

    const underlayArtifacts = DxfFrameBuilder.buildUnderlayImageArtifacts(
      frameOptions,
      context.modelScale
    )
    registerFrameImage(drawing, underlayArtifacts)

    // 以图框边界作为视口范围，确保打开文件时视图恰好覆盖整个图框
    const frameBounds = {
      min: { x: frameOriginX, y: frameOriginY },
      max: { x: frameOriginX + W, y: frameOriginY + H },
    }
    DxfExporter.setViewport(drawing, entities, context.modelScale, frameBounds)

    return drawing.toDxfString()
  }

  /**
   * 构建 Drawing 对象，并将 ICADMetadata 中的图层与图元写入其中。
   */
  private static buildDrawing(
    data: ICADMetadata,
    context: DxfExportContext
  ): { drawing: Drawing; entities: IEntity[] } {
    const drawing = new Drawing()
    // 设置单位
    drawing.setUnits(context.dxfUnit)
    if (context.lengthDisplayFormat !== undefined) {
      drawing.header('LUNITS', [[70, context.lengthDisplayFormat]])
    }
    if (context.lengthDisplayPrecision !== undefined) {
      drawing.header('LUPREC', [[70, context.lengthDisplayPrecision]])
    }
    drawing.header('ACADVER', [[1, 'AC1024']])
    drawing.header('PROXYGRAPHICS', [[70, 1]])
    // 设置标注样式
    const textStyle = drawing.tables['STYLE'].elements[0]
    drawing.tables?.['DIMSTYLE']?.add(
      new DimStyle(
        DimStyleName,
        textStyle.handle,
        context.formatsMeterSource ? DxfExporter.getDimensionStyleOptions(context) : undefined
      )
    )
    // 添加自定义复杂线型
    drawing.lineTypes['DOUBLEDASH'] = getComplexLineType(textStyle.handle) as unknown as LineType

    const { layers = [], entities = [] } = data || {}
    const layerMap = new Map<string, Layer>()

    for (const layer of layers) {
      if (IgnoreLayerSet.has(layer.name)) {
        continue
      }
      layerMap.set(layer.id, layer as Layer)
      drawing.addLayer(
        layer.name,
        DxfExporter.colorToACI(layer.color),
        DxfExporter.getLineTypeName(layer.lineType)
      )
      drawing.layers[layer.name].setTrueColor(layer.color)
    }

    for (const entity of entities) {
      const layer = layerMap.get(entity.layerId)
      const layerName = layer?.name ?? '0'
      drawing.setActiveLayer(layerName)
      const shape = DxfExporter.exportEntity(drawing, entity, context)
      DxfExporter.setShapeBaseData(shape, entity)
    }

    return { drawing, entities }
  }

  private static exportEntity(drawing: Drawing, entity: IEntity, context: DxfExportContext) {
    const scale = context.modelScale
    switch (entity.type) {
      case EntityType.Polyline:
        return DxfExporter.exportPolyline(drawing, entity as PolylineEntity, scale)
      case EntityType.Arc:
        return DxfExporter.exportArc(drawing, entity as ArcEntity, scale)
      case EntityType.Circle:
        return DxfExporter.exportCircle(drawing, entity as CircleEntity, scale)
      case EntityType.Spline:
        return DxfExporter.exportSpline(drawing, entity as SplineEntity, scale)
      case EntityType.Ellipse:
        return DxfExporter.exportEllipse(drawing, entity as EllipseEntity, scale)
      case EntityType.Ray:
        return DxfExporter.exportRay(drawing, entity as RayEntity, scale)
      case EntityType.MText:
        return DxfExporter.exportMText(drawing, entity as MTextEntity, scale)
      case EntityType.AlignDimension:
        return DxfExporter.exportAlignDimension(drawing, entity as AlignDimensionEntity, context)
      case EntityType.MLeader:
        return DxfExporter.exportMLeader(drawing, entity as MLeaderEntity, scale)
      case EntityType.AngularDimension:
        return DxfExporter.exportAngularDimension(drawing, entity as AngularDimensionEntity, scale)
      default:
        log.warn(`[DxfExporter] Unsupported entity type: ${entity.type}`)
    }
  }

  private static exportPolyline(
    drawing: Drawing,
    entity: PolylineEntity,
    scale: number = 1
  ): Polyline | undefined {
    if (!entity.vertices || entity.vertices.length === 0) return
    const shape = new Polyline(
      entity.vertices.map((v) => [v.x * scale, v.y * scale, v.bulge || 0] as any),
      entity.isClosed
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportArc(drawing: Drawing, entity: ArcEntity, scale: number = 1) {
    let startAngleDeg = (entity.startAngle * 180) / Math.PI
    let endAngleDeg = (entity.endAngle * 180) / Math.PI
    if (entity.clockwise) {
      ;[startAngleDeg, endAngleDeg] = [endAngleDeg, startAngleDeg]
    }

    const shape = new Arc(
      entity.center.x * scale,
      entity.center.y * scale,
      entity.radius * scale,
      startAngleDeg,
      endAngleDeg
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportCircle(drawing: Drawing, entity: CircleEntity, scale: number = 1) {
    const shape = new Circle(
      entity.center.x * scale,
      entity.center.y * scale,
      entity.radius * scale
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportSpline(drawing: Drawing, entity: SplineEntity, scale: number = 1) {
    const shape = new Spline(
      entity.controlPoints.map((p) => [p.x * scale, p.y * scale]),
      entity.degree,
      entity.knots,
      entity.weights,
      entity.fitPoints?.map((p) => [p.x * scale, p.y * scale])
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportEllipse(drawing: Drawing, entity: EllipseEntity, scale: number = 1) {
    const shape = new Ellipse(
      entity.center.x * scale,
      entity.center.y * scale,
      entity.majorAxisEndPoint.x * scale,
      entity.majorAxisEndPoint.y * scale,
      entity.axisRatio,
      entity.startAngle,
      entity.endAngle
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportRay(drawing: Drawing, entity: RayEntity, scale: number = 1) {
    const shape = new Ray(
      { x: entity.startPoint.x * scale, y: entity.startPoint.y * scale },
      { x: entity.unitVector.x, y: entity.unitVector.y }
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportMText(drawing: Drawing, entity: MTextEntity, scale: number = 1) {
    const { x, y } = entity.position
    const rotationDeg = (entity.rotation * 180) / Math.PI
    const shape = new MText(
      x * scale,
      y * scale,
      entity.textHeight * scale,
      rotationDeg,
      entity.contents,
      entity.rectWidth !== undefined ? entity.rectWidth * scale : entity.rectWidth,
      entity.attachment
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportAlignDimension(
    drawing: Drawing,
    entity: AlignDimensionEntity,
    context: DxfExportContext
  ) {
    const renderData = DimensionUtils.getAlignedDimensionRenderData(entity, context)
    const lineType = DxfExporter.getLineTypeName(entity.lineType)
    const lineTypeHandle = (drawing.lineTypes[lineType] as any)?.handle
    const blockName = DxfExporter.createBlockForDimension(drawing, entity, renderData)
    const shape = new AlignDimension(
      renderData.p1,
      renderData.p2,
      renderData.offsetPoint,
      renderData.textMidPoint,
      renderData.dimensionText,
      renderData.textHeight,
      renderData.textGap,
      renderData.arrowSize,
      renderData.hasUserDefinedTextPosition,
      lineTypeHandle,
      blockName
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static exportMLeader(drawing: Drawing, entity: MLeaderEntity, scale: number = 1) {
    drawing.registerMLeaderEntity()
    const toScaledPoint = (point: { x: number; y: number }) => ({
      x: point.x * scale,
      y: point.y * scale,
    })
    const branches = entity.branches.map((branch) =>
      DxfExporter.getMLeaderBranchExportData(
        getMLeaderBranchPoints(branch).map(toScaledPoint),
        entity.hasLanding
      )
    )

    const textStyle = drawing.tables['STYLE'].elements[0] as unknown as { handle: string }
    const lineTypeName = DxfExporter.getLineTypeName(entity.lineType)
    const textPosition = entity.isTextPositionEdited
      ? entity.textPosition
      : DxfExporter.getMLeaderExportTextPosition(entity)
    const textLandingGap = DxfExporter.getMLeaderExportLandingGap(entity, scale)
    const shape = new MLeader(
      toScaledPoint(textPosition),
      entity.textHeight * scale,
      entity.contents,
      textLandingGap,
      entity.hasLanding,
      branches,
      drawing.getMLeaderStyleHandle(),
      textStyle.handle,
      (drawing.lineTypes[lineTypeName] as unknown as { handle?: string })?.handle
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static getMLeaderBranchExportData(
    points: { x: number; y: number }[],
    hasLanding: boolean
  ) {
    const fallbackPoint = points[0] ?? { x: 0, y: 0 }
    const landingEnd = hasLanding && points.length > 2 ? points[points.length - 1] : undefined
    const lastLeaderLinePoint = landingEnd
      ? (points[points.length - 2] ?? fallbackPoint)
      : (points[points.length - 1] ?? fallbackPoint)
    const leaderLinePoints = landingEnd ? points.slice(0, -2) : points.slice(0, -1)
    const safeLeaderLinePoints = leaderLinePoints.length > 0 ? leaderLinePoints : [fallbackPoint]
    const doglegEndPoint = landingEnd ?? lastLeaderLinePoint
    const dx = doglegEndPoint.x - lastLeaderLinePoint.x
    const dy = doglegEndPoint.y - lastLeaderLinePoint.y
    const doglegLength = Math.hypot(dx, dy)
    const doglegVector =
      doglegLength > 1e-6
        ? { x: dx / doglegLength, y: dy / doglegLength }
        : DxfExporter.getFallbackMLeaderDoglegVector([...safeLeaderLinePoints, lastLeaderLinePoint])

    return {
      points: safeLeaderLinePoints,
      lastLeaderLinePoint,
      doglegVector,
      doglegLength: hasLanding ? Math.max(doglegLength, 1e-6) : 0,
    }
  }

  private static getFallbackMLeaderDoglegVector(points: { x: number; y: number }[]) {
    const lastPoint = points[points.length - 1]
    const previousPoint = points[points.length - 2]
    if (lastPoint && previousPoint && lastPoint.x < previousPoint.x) {
      return { x: -1, y: 0 }
    }
    return { x: 1, y: 0 }
  }

  private static getMLeaderExportTextPosition(entity: MLeaderEntity) {
    const branch = entity.branches[0]
    const landingEnd = getMLeaderLandingEnd(branch)
    if (!branch || !landingEnd) {
      return entity.textPosition
    }

    const landingVector = getMLeaderLandingVector(branch)
    const textGap = Math.abs(entity.landingGap ?? 0)
    return {
      x: landingEnd.x + landingVector.x * textGap,
      y: landingEnd.y + landingVector.y * textGap,
    }
  }

  private static getMLeaderExportLandingGap(entity: MLeaderEntity, scale: number) {
    return Math.abs(entity.landingGap ?? 0) * scale
  }

  private static exportAngularDimension(
    drawing: Drawing,
    entity: AngularDimensionEntity,
    scale: number = 1
  ) {
    const dimensionData = getAngularDimensionData(
      entity.line1,
      entity.line2,
      entity.arcPoint,
      entity.textPosition
    )
    if (!dimensionData) {
      return undefined
    }

    const blockName = DxfExporter.createBlockForAngularDimension(
      drawing,
      entity,
      dimensionData,
      scale
    )
    const toScaledPoint = (point: { x: number; y: number }) => ({
      x: point.x * scale,
      y: point.y * scale,
    })
    const shape = new AngularDimension(
      toScaledPoint(entity.line1.startPoint),
      toScaledPoint(entity.line1.endPoint),
      toScaledPoint(entity.line2.startPoint),
      toScaledPoint(entity.line2.endPoint),
      toScaledPoint(entity.arcPoint),
      toScaledPoint(entity.textPosition),
      entity.dimensionText,
      entity.isTextPositionEdited,
      blockName
    ) as any
    drawing.activeLayer?.addShape(shape)
    return shape
  }

  private static createBlockForAngularDimension(
    drawing: Drawing,
    entity: AngularDimensionEntity,
    dimensionData: NonNullable<ReturnType<typeof getAngularDimensionData>>,
    scale: number = 1
  ) {
    const blockName = drawing.getAnonDimBlockName()
    const block = new Block(blockName, 1)
    drawing.blocks[blockName] = block

    const layer = drawing.activeLayer!
    const lineType = DxfExporter.getLineTypeName(entity.lineType)
    const lineWidth = DxfExporter.lineWidthToACI(entity.lineWidth)
    const trueColor = entity.colorMethod === ColorMethod.RGB ? entity.color : undefined
    const applyBaseStyle = (shape: {
      layer?: unknown
      lineType?: string
      lineWidth?: number
      trueColor?: number
    }) => {
      shape.layer = layer
      shape.lineType = lineType
      shape.lineWidth = lineWidth
      if (trueColor !== undefined) {
        shape.trueColor = trueColor
      }
    }

    const startAngleDeg = (dimensionData.startAngle * 180) / Math.PI
    const endAngleDeg = (dimensionData.endAngle * 180) / Math.PI
    const toScaledPoint = (point: { x: number; y: number }) => ({
      x: point.x * scale,
      y: point.y * scale,
    })
    const addLegLine = (
      startPoint: { x: number; y: number },
      endPoint: { x: number; y: number }
    ) => {
      const line = new Line(startPoint, endPoint)
      applyBaseStyle(line)
      block.addShape(line)
    }
    const legLines = getAngularDimensionLegLines(entity, dimensionData)
    for (const line of legLines) {
      addLegLine(toScaledPoint(line.startPoint), toScaledPoint(line.endPoint))
    }

    const arc = new Arc(
      dimensionData.intersection.x * scale,
      dimensionData.intersection.y * scale,
      dimensionData.radius * scale,
      startAngleDeg,
      endAngleDeg
    )
    applyBaseStyle(arc)
    block.addShape(arc)

    const textHeight = (entity.textHeight ?? 2.5) * scale
    const displayText = entity.dimensionText || `${dimensionData.angleDegrees.toFixed(2)}°`
    const text = new MText(
      entity.textPosition.x * scale,
      entity.textPosition.y * scale,
      textHeight,
      0,
      displayText,
      Math.max(textHeight * 2, displayText.length * textHeight),
      5
    ) as any
    applyBaseStyle(text)
    block.addShape(text)

    return blockName
  }

  private static createBlockForDimension(
    drawing: Drawing,
    entity: AlignDimensionEntity,
    renderData: ReturnType<typeof DimensionUtils.getAlignedDimensionRenderData>
  ) {
    // 创建匿名块
    const blockName = drawing.getAnonDimBlockName()
    const block = new Block(blockName, 1)
    drawing.blocks[blockName] = block

    const {
      d1,
      d2,
      e1_start,
      e1_end,
      e2_start,
      e2_end,
      ux,
      uy,
      displayText,
      textHeight,
      textMidPoint,
      textRotationDeg,
      arrowSize,
    } = renderData
    const layer = drawing.activeLayer!
    const addSeg = (a: { x: number; y: number }, b: { x: number; y: number }) => {
      const seg = new Line(a, b)
      seg.layer = layer
      DxfExporter.setShapeBaseData(seg, entity)
      block.addShape(seg)
    }
    const addSolid = (
      sp1: { x: number; y: number },
      sp2: { x: number; y: number },
      sp3: { x: number; y: number }
    ) => {
      const solid = new Solid(sp1, sp2, sp3)
      solid.layer = layer
      DxfExporter.setShapeBaseData(solid, entity)
      block.addShape(solid)
    }

    // 延伸线 + 标注线
    addSeg(e1_start, e1_end)
    addSeg(e2_start, e2_end)
    addSeg(d1, d2)

    // 实心三角箭头（与 AutoCAD 默认 closed-filled 箭头一致）
    const arrowHalfWidth = arrowSize / 6
    const addArrow = (tip: { x: number; y: number }, dirX: number, dirY: number) => {
      const tailCenterX = tip.x + dirX * arrowSize
      const tailCenterY = tip.y + dirY * arrowSize
      const perpX = -dirY
      const perpY = dirX
      addSolid(
        tip,
        { x: tailCenterX + perpX * arrowHalfWidth, y: tailCenterY + perpY * arrowHalfWidth },
        { x: tailCenterX - perpX * arrowHalfWidth, y: tailCenterY - perpY * arrowHalfWidth }
      )
    }

    addArrow(d1, ux, uy)
    addArrow(d2, -ux, -uy)

    const text = new MText(
      textMidPoint.x,
      textMidPoint.y,
      textHeight,
      textRotationDeg,
      displayText,
      Math.max(textHeight * 2, displayText.length * textHeight),
      5
    ) as any
    text.layer = layer
    DxfExporter.setShapeBaseData(text, entity)
    block.addShape(text)

    return blockName
  }

  private static getDimensionStyleOptions(context: DxfExportContext) {
    const { displayUnit, feetFormat } = context.unitPreference
    if (displayUnit === 'ft' && feetFormat === 'feet-inches') {
      return {
        lengthFactor: 1,
        precision: 4,
        linearUnitFormat: 4 as const,
        fractionFormat: 2 as const,
      }
    }

    return {
      lengthFactor: 1,
      precision: DIMENSION_PRECISIONS[displayUnit],
      linearUnitFormat: 2 as const,
    }
  }

  private static colorToACI(color: number): number {
    const aci = Drawing.ACI ?? {
      RED: 1,
      YELLOW: 2,
      GREEN: 3,
      CYAN: 4,
      BLUE: 5,
      MAGENTA: 6,
      WHITE: 7,
    }
    const map: Record<number, number> = {
      16711680: aci.RED,
      65280: aci.GREEN,
      255: aci.BLUE,
      65535: aci.CYAN,
      16711935: aci.MAGENTA,
      16776960: aci.YELLOW,
      16777215: aci.WHITE,
    }

    return map[color] ?? aci.WHITE
  }

  private static getLineTypeName(lineType: string): string {
    return LineTypeMapping[lineType] || LineTypeMapping.default
  }

  private static lineWidthToACI(lineWidth: number) {
    // DXF lineWidth in hundredths of mm
    // 若不在标准线宽列表中，则返回 ByLayer
    return CADStandardLineWidth.has(lineWidth) ? lineWidth * 100 : DXFLineWidth.ByLayer
  }

  private static setShapeBaseData(
    shape: { lineType?: string; lineWidth?: number; trueColor?: number },
    entity: IEntity
  ) {
    if (!shape) return

    shape.lineType = DxfExporter.getLineTypeName(entity.lineType)
    shape.lineWidth = DxfExporter.lineWidthToACI(entity.lineWidth)
    if (entity.colorMethod === ColorMethod.RGB) {
      shape.trueColor = entity.color
    }
  }

  /**
   * 计算实体的边界框
   * @param entities 实体数组
   * @returns 包含最小值和最大值的边界框,如果没有实体则返回null
   */
  private static calculateBounds(
    entities: IEntity[],
    scale: number = 1
  ): { min: { x: number; y: number }; max: { x: number; y: number } } | null {
    if (!entities || entities.length === 0) {
      return null
    }

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    const updateBounds = (x: number, y: number) => {
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }

    for (const entity of entities) {
      switch (entity.type) {
        case EntityType.Polyline: {
          const polyline = entity as PolylineEntity
          polyline.vertices?.forEach((v) => updateBounds(v.x, v.y))
          break
        }
        case EntityType.Arc: {
          DxfExporter.updateArcBounds(entity as ArcEntity, updateBounds)
          break
        }
        case EntityType.Circle: {
          const { center, radius } = entity as CircleEntity
          updateBounds(center.x - radius, center.y - radius)
          updateBounds(center.x + radius, center.y + radius)
          break
        }
        case EntityType.Spline: {
          const spline = entity as SplineEntity
          spline.controlPoints?.forEach((p) => updateBounds(p.x, p.y))
          spline.fitPoints?.forEach((p) => updateBounds(p.x, p.y))
          break
        }
        case EntityType.Ellipse: {
          const ellipse = entity as EllipseEntity
          const rotation = ellipse.rotation
          const radiusX = ellipse.radiusX
          const radiusY = ellipse.radiusY
          const halfWidth = Math.sqrt(
            (radiusX * Math.cos(rotation)) ** 2 + (radiusY * Math.sin(rotation)) ** 2
          )
          const halfHeight = Math.sqrt(
            (radiusX * Math.sin(rotation)) ** 2 + (radiusY * Math.cos(rotation)) ** 2
          )
          updateBounds(ellipse.center.x - halfWidth, ellipse.center.y - halfHeight)
          updateBounds(ellipse.center.x + halfWidth, ellipse.center.y + halfHeight)
          break
        }
        case EntityType.Ray: {
          const ray = entity as RayEntity
          updateBounds(ray.startPoint.x, ray.startPoint.y)
          break
        }
        case EntityType.MText: {
          const mtext = entity as MTextEntity
          updateBounds(mtext.position.x, mtext.position.y)
          break
        }
        case EntityType.MLeader: {
          const leader = entity as MLeaderEntity
          updateBounds(leader.textPosition.x, leader.textPosition.y)
          leader.branches.forEach((branch) => {
            updateBounds(branch.arrowPoint.x, branch.arrowPoint.y)
            branch.vertices.forEach((vertex) => updateBounds(vertex.x, vertex.y))
          })
          break
        }
        case EntityType.AlignDimension: {
          const dim = entity as AlignDimensionEntity
          updateBounds(dim.p1.x, dim.p1.y)
          updateBounds(dim.p2.x, dim.p2.y)
          updateBounds(dim.offsetPoint.x, dim.offsetPoint.y)
          updateBounds(dim.textPosition.x, dim.textPosition.y)
          break
        }
        case EntityType.AngularDimension: {
          const dim = entity as AngularDimensionEntity
          const dimensionData = getAngularDimensionData(
            dim.line1,
            dim.line2,
            dim.arcPoint,
            dim.textPosition
          )
          if (!dimensionData) {
            break
          }
          const legLines = getAngularDimensionLegLines(dim, dimensionData)
          updateBounds(dimensionData.intersection.x, dimensionData.intersection.y)
          updateBounds(dimensionData.arcStart.x, dimensionData.arcStart.y)
          updateBounds(dimensionData.arcEnd.x, dimensionData.arcEnd.y)
          legLines.forEach((line) => {
            updateBounds(line.startPoint.x, line.startPoint.y)
            updateBounds(line.endPoint.x, line.endPoint.y)
          })
          updateBounds(dim.arcPoint.x, dim.arcPoint.y)
          updateBounds(dim.textPosition.x, dim.textPosition.y)
          break
        }
      }
    }

    if (
      !Number.isFinite(minX) ||
      !Number.isFinite(minY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY)
    ) {
      return null
    }

    const finalScale = scale <= 0 ? 1 : scale
    return {
      min: { x: minX * finalScale, y: minY * finalScale },
      max: { x: maxX * finalScale, y: maxY * finalScale },
    }
  }

  private static updateArcBounds(arc: ArcEntity, updateBounds: (x: number, y: number) => void) {
    const { center, radius, startAngle, endAngle } = arc
    if (
      !Number.isFinite(center.x) ||
      !Number.isFinite(center.y) ||
      !Number.isFinite(radius) ||
      radius < 0
    ) {
      return
    }

    const updatePointAtAngle = (angle: number) => {
      updateBounds(center.x + radius * Math.cos(angle), center.y + radius * Math.sin(angle))
    }

    updatePointAtAngle(startAngle)
    updatePointAtAngle(endAngle)

    const cardinalAngles = [0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2]
    for (const angle of cardinalAngles) {
      if (DxfExporter.isAngleOnArc(angle, arc)) {
        updatePointAtAngle(angle)
      }
    }
  }

  private static isAngleOnArc(angle: number, arc: ArcEntity): boolean {
    const sweepAngle = ArcUtils.getSweepAngle(arc.startAngle, arc.endAngle, arc.clockwise)
    if (sweepAngle >= Math.PI * 2 - Number.EPSILON) {
      return true
    }

    const distanceFromStart = arc.clockwise
      ? DxfExporter.normalizePositiveAngle(arc.startAngle - angle)
      : DxfExporter.normalizePositiveAngle(angle - arc.startAngle)

    return distanceFromStart <= sweepAngle + Number.EPSILON
  }

  private static normalizePositiveAngle(angle: number): number {
    const tau = Math.PI * 2
    const normalized = angle % tau
    return normalized < 0 ? normalized + tau : normalized
  }

  /**
   * 设置视口边界
   */
  private static setViewport(
    drawing: Drawing,
    entities: IEntity[],
    scale: number = 1,
    overrideBounds?: { min: { x: number; y: number }; max: { x: number; y: number } }
  ) {
    // 当前视口
    const activeVport = (drawing as any).tables?.['VPORT']?.elements?.find(
      (ele) => ele.name === '*ACTIVE'
    )
    if (!activeVport) {
      return
    }

    const bounds = overrideBounds ?? DxfExporter.calculateBounds(entities, scale)
    if (!bounds) {
      return
    }

    const aspectRatio = 16 / 9
    const deltaX = bounds.max.x - bounds.min.x
    const deltaY = bounds.max.y - bounds.min.y
    activeVport.height = Math.max(deltaX / aspectRatio, deltaY) * 1.2 // 适当放大边界
    const originalTags = activeVport.tags.bind(activeVport)
    activeVport.tags = (manager: any) => {
      originalTags(manager)
      manager.point(0, 0) // 视口左下角
      manager.push(11, 1) // 视口右上角(通常为1,1，代表全屏)
      manager.push(21, 1)
      manager.push(12, (bounds.max.x + bounds.min.x) / 2) // 视口中心
      manager.push(22, (bounds.max.y + bounds.min.y) / 2)
      manager.push(41, aspectRatio) // 纵横比
      manager.push(45, activeVport.height) // 视口高度
    }
  }

  /**
   * 将 DXF 字符串触发为浏览器文件下载。
   *
   * @param filename - 下载文件名，未带 .dxf 后缀时自动补齐
   * @param content - 完整的 DXF 文本
   */
  static downloadDxf(filename: string, content: string): void {
    const blob = new Blob([content], { type: 'application/dxf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename.endsWith('.dxf') ? filename : `${filename}.dxf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}
